'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';

interface ServiceFeature {
  title: string;
  description: string;
}

interface Service {
  title: string;
  icon: string;
  description: string;
  features: ServiceFeature[];
  caseStudies: CaseStudy[];
  technicalSpecs: string[];
}

export default function ServicesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilter] = useState<'all' | 'forensics' | 'security' | 'compliance'>('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
  {
    title: "Digital Forensics Investigation",
    icon: "MagnifyingGlassIcon",
    description: "Comprehensive digital evidence collection and analysis for legal proceedings, internal investigations, and incident response. Our forensics team employs government-certified methodologies to ensure evidence integrity and court admissibility.",
    features: [
    { title: "Evidence Preservation", description: "Chain-of-custody documentation and forensically sound data acquisition using write-blocking technology" },
    { title: "Data Recovery", description: "Advanced recovery techniques for deleted, encrypted, or damaged digital evidence across all storage media" },
    { title: "Timeline Analysis", description: "Detailed reconstruction of digital events with precise timestamps and user attribution" },
    { title: "Expert Testimony", description: "Court-qualified forensics experts providing clear, defensible testimony in legal proceedings" },
    { title: "Multi-Device Analysis", description: "Comprehensive examination across computers, mobile devices, cloud storage, and IoT systems" }],
    technicalSpecs: [
    "EnCase Forensic v21.x certified analysis platform",
    "FTK Imager for forensically sound data acquisition",
    "X-Ways Forensics for advanced file carving and recovery",
    "Cellebrite UFED for mobile device extraction",
    "Magnet AXIOM for comprehensive artifact analysis",
    "Write-blocking hardware for evidence preservation"]

  },
  {
    title: "Security Consulting",
    icon: "ShieldExclamationIcon",
    description: "We help organizations proactively uncover and close security gaps across assets, networks, and identities by delivering end-to-end cybersecurity services—from assessment and scanning to implementation—for government and commercial enterprises.",
features: [
  {
    title: "Security Posture Assessment",
    description: "Comprehensive evaluation of IT assets, networks, and identities to identify control gaps and risk exposure"
  },
  {
    title: "Vulnerability Assessment & Penetration Testing (VAPT)",
    description: "Systematic discovery and validation of vulnerabilities across infrastructure, applications, and cloud environments"
  },
  {
    title: "Identity & Access Management (IAM) Review",
    description: "Assessment of user access, privilege escalation risks, and identity governance controls"
  },
  {
    title: "Network & Infrastructure Security Review",
    description: "Analysis of network architecture, firewall rules, segmentation, and secure configuration practices"
  },
  {
    title: "Security Architecture & Implementation Advisory",
    description: "Design and guidance for implementing security controls aligned with business and regulatory requirements"
  },
  {
    title: "Compliance & Risk Advisory",
    description: "Mapping security controls to standards such as ISO 27001, DPDPA, and sector-specific regulations"
  }
],
technicalSpecs: [
  "Nessus, and OpenVAS for vulnerability scanning",
  "Burp Suite and OWASP ZAP for application security testing",
  "Device security assessment tools",
  "Firewall and network configuration analysis",
  "Frameworks for threat and risk modeling",
  "ISO 27001, and DPDP compliance frameworks"
]

  },
  {
    title: "EULA Compliance",
    icon: "DevicePhoneMobileIcon",
    description: "We help organizations audit and ensure EULA compliance by delivering expert-driven evaluations that align software usage with legal obligations, cybersecurity objectives, and future institutional strategies.",
features: [
  {
    title: "Software License Usage Audit",
    description: "Assessment of installed software against license entitlements to identify overuse, misuse, and shadow IT"
  },
  {
    title: "EULA Risk & Violation Analysis",
    description: "Detailed review of end-user license terms to detect non-compliance, legal exposure, and contractual breaches"
  },
  {
    title: "Third-Party Software Compliance Review",
    description: "Evaluation of vendor, open-source, and commercial software usage against contractual and regulatory requirements"
  },
  {
    title: "BYOD & Endpoint License Governance",
    description: "Assessment of software usage on employee-owned and enterprise endpoints for license and policy compliance"
  },
  {
    title: "Audit-Ready Compliance Reporting",
    description: "Structured compliance reports with findings, risk ratings, and remediation recommendations"
  },
  {
    title: "Future Licensing & Procurement Advisory",
    description: "Strategic guidance to optimize licensing models, reduce costs, and prevent future compliance violations"
  }
],

technicalSpecs: [
  "Software Asset Management (SAM) tools for license discovery and usage tracking",
  "Endpoint inventory and application discovery platforms",
  "Vendor EULA and contract clause analysis frameworks",
  "ISO/IEC standards for IT asset and license management",
  "Policy mapping aligned with DPDP, ISO 27001, and internal governance controls",
  "Risk scoring and compliance maturity assessment models"
]
  },
  {
    title: "Cyber Risk Auditing",
    icon: "BoltIcon",
    description: "We conduct cyber risk audits that systematically assess prevention, detection, and response controls to help organizations continuously reduce security risk and adapt their defenses as threat techniques evolve.",
features: [
  {
    title: "Risk Identification & Asset Criticality Mapping",
    description: "Identification of critical assets, data flows, and business processes to determine risk exposure and impact"
  },
  {
    title: "Preventive Control Assessment",
    description: "Evaluation of security controls such as access control, patching, configuration management, and hardening"
  },
  {
    title: "Detection & Monitoring Capability Review",
    description: "Assessment of logging, monitoring, alerting, and SIEM effectiveness for timely threat detection"
  },
  {
    title: "Incident Response & Readiness Audit",
    description: "Review of incident response plans, roles, escalation procedures, and tabletop readiness"
  },
  {
    title: "Risk Scoring & Prioritization",
    description: "Quantitative and qualitative risk scoring based on likelihood, impact, and control maturity"
  },
  {
    title: "Executive Risk Reporting",
    description: "Clear, audit-ready reports translating technical risks into business and compliance impact"
  }
],

technicalSpecs: [
  "NIST Cybersecurity Framework (CSF) for risk-based assessment",
  "ISO/IEC 27005 for information security risk management",
  "MITRE ATT&CK for threat likelihood mapping",
  "Risk registers and control maturity models",
  "SIEM and log management review methodologies",
  "Board-level and regulator-ready risk reporting templates"
]

  },
  {
    title: "Data Privacy Compliance & Audit Support",
    icon: "DocumentCheckIcon",
    description: "We deliver data protection and privacy compliance audits that evaluate policies, access controls, and risk management, while forensically identifying data breaches and supporting remediation, gap analysis, and regulatory reporting.",
features: [
  {
    title: "Privacy Compliance Gap Analysis",
    description: "Assessment of organizational controls against data protection regulations to identify compliance gaps and risk exposure"
  },
  {
    title: "Policy & Governance Review",
    description: "Evaluation and enhancement of data protection policies, privacy notices, retention practices, and consent mechanisms"
  },
  {
    title: "Access Control & Data Handling Audit",
    description: "Review of user access rights, data classification, processing activities, and third-party data sharing"
  },
  {
    title: "Breach Identification & Impact Assessment",
    description: "Forensic identification of data breaches with assessment of scope, affected data, and regulatory impact"
  },
  {
    title: "Regulatory Reporting & Audit Support",
    description: "Support for breach notification, regulator communication, and audit-ready documentation"
  },
  {
    title: "Remediation & Compliance Roadmap",
    description: "Actionable remediation plan with prioritized controls to achieve and sustain compliance"
  }
],

technicalSpecs: [
  "Data protection frameworks aligned with DPDPA, GDPR, and ISO/IEC 27701",
  "NIST Privacy Framework and ISO 27001/27002 control mapping",
  "Access control and identity review methodologies",
  "Forensic data analysis for breach validation and evidence support",
  "Risk registers and privacy impact assessment (PIA/DPIA) templates",
  "Audit-ready compliance and regulatory reporting formats"
]

  },
  {
    title: "Security Training & Awareness",
    icon: "AcademicCapIcon",
    description: "Customized cybersecurity training programs for organizations of all sizes. From executive briefings to technical deep-dives, we build security awareness and technical capabilities aligned with your specific threat landscape and compliance requirements.",
    features: [
    { title: "Custom Curriculum Development", description: "Tailored training programs addressing your organization's specific security challenges and industry requirements" },
    { title: "Role-Based Training", description: "Specialized content for executives, IT staff, developers, and end users with appropriate technical depth" },
    { title: "Hands-On Labs", description: "Practical exercises and simulations providing real-world experience with security tools and incident response" },
    { title: "Phishing Simulations", description: "Realistic phishing campaigns with detailed reporting to measure and improve security awareness" },
    { title: "Certification Preparation", description: "Training programs aligned with industry certifications including CISM, CEH, and CHFI" }],
    technicalSpecs: [
    "PortSwigger Labs platform for Vulnerability Simulation and training",
    "Custom learning management system integration",
    "Interactive scenario-based training modules",
    "Detailed analytics and reporting dashboards",
    "Mobile-friendly training content delivery",
    "Certification tracking and compliance reporting"]
  }];

  const filteredServices = services.filter((service) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'forensics') return ['Digital Forensics Investigation', 'Mobile Device Forensics', 'Cybercrime Investigation'].includes(service.title);
    if (activeFilter === 'security') return ['Incident Response & Recovery', 'Security Training & Awareness'].includes(service.title);
    if (activeFilter === 'compliance') return service.title === 'Compliance & Audit Support';
    return true;
  });

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:239:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="239" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:240:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="240" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-24%20pb-16%22%7D" className="pt-24 pb-16">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:241:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="241" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div data-component-id="src/app/services/components/ServicesInteractive.tsx:242:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="242" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
              <div data-component-id="src/app/services/components/ServicesInteractive.tsx:243:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="243" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded-xl%22%7D" className="h-32 bg-muted rounded-xl" />
              <div data-component-id="src/app/services/components/ServicesInteractive.tsx:244:14" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="244" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div data-component-id="src/app/services/components/ServicesInteractive.tsx:245:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="245" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" className="h-96 bg-muted rounded-xl" />
                <div data-component-id="src/app/services/components/ServicesInteractive.tsx:246:16" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="246" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" className="h-96 bg-muted rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/services/components/ServicesInteractive.tsx:256:4" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="256" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/services/components/ServicesInteractive.tsx:257:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="257" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-24%20pb-16%22%7D" className="pt-24 pb-16">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:258:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="258" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:259:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="259" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h1 data-component-id="src/app/services/components/ServicesInteractive.tsx:260:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="260" data-component-file="ServicesInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20lg%3Atext-5xl%20font-bold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Cybersecurity%20Services%22%7D" className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Cybersecurity Services
            </h1>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:263:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="263" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Government-level%20expertise%20for%20enterprise-scale%20challenges.%20Comprehensive%20digital%20forensics%2C%20incident%20response%2C%20and%20compliance%20services%20backed%20by%20real-world%20investigative%20experience.%22%7D" className="text-lg text-text-secondary max-w-3xl mx-auto">
              Government-level expertise for enterprise-scale challenges. Comprehensive digital forensics, incident response, and compliance services backed by real-world investigative experience.
            </p>
          </div>

          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:268:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="268" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%20mb-16%22%7D" className="space-y-8 mb-16">
            {filteredServices.map((service, index) =>
            <ServiceCard data-component-id="src/app/services/components/ServicesInteractive.tsx:270:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="270" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={index} {...service} />
            )}
          </div>


          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:275:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="275" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-16%20bg-primary%20text-white%20rounded-xl%20p-8%20lg%3Ap-12%20text-center%22%7D" className="mt-16 bg-primary text-white rounded-xl p-8 lg:p-12 text-center">
            <h2 data-component-id="src/app/services/components/ServicesInteractive.tsx:276:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="276" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-bold%20mb-4%20text-white%22%2C%22textContent%22%3A%22Ready%20to%20Enhance%20Your%20Security%20Posture%3F%22%7D" className="text-3xl font-bold mb-4 text-white">Ready to Enhance Your Security Posture?</h2>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:277:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="277" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20mb-8%20opacity-90%20max-w-2xl%20mx-auto%20text-white%2F90%22%2C%22textContent%22%3A%22Schedule%20a%20consultation%20with%20our%20cybersecurity%20experts%20to%20discuss%20your%20specific%20requirements%20and%20develop%20a%20customized%20security%20strategy.%22%7D" className="text-lg mb-8 opacity-90 max-w-2xl mx-auto text-white/90">
              Schedule a consultation with our cybersecurity experts to discuss your specific requirements and develop a customized security strategy.
            </p>
          </div>
        </div>
      </div>
    </div>);

}